<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ============================================================
  // Public user profile page (viewing other users)

  import FollowButton from '../../components/ui/FollowButton.svelte';
  import BlockButton from '../../components/ui/BlockButton.svelte';
  import { apiClient } from '../../utils/api';

  interface UserProfile {
    id: number;
    name: string;
    nickname?: string;
    // 🔴 전부 snake_case 다 — `users_controller#show` 가 그렇게 내려준다.
    //    이 파일은 원래 카멜로 읽고 있었고, 그래서 아바타·가입일·팔로우/차단
    //    상태·본인 분기·통계·소속이 **전부 한 번도 그려진 적이 없었다**.
    //    Inertia props 를 내리는 컨트롤러 36개 중 33개가 snake 전용이라
    //    정본은 컨트롤러 쪽이다(실측). 프론트를 맞춘다.
    avatar_url?: string;
    bio?: string;
    organization_name?: string;
    department?: string;
    team?: string;
    verified: boolean;
    created_at: string;
    stats?: {
      posts_count: number;
      comments_count: number;
      likes_count: number;
      followers_count: number;
      following_count: number;
    };
    is_following?: boolean;
    is_blocked?: boolean;
    is_own_profile?: boolean;
  }

  let { user = {} as UserProfile }: { user: UserProfile } = $props();

  // 소속 · 부서 · 팀. 셋 다 선택 입력이라 채워진 것만 이어 붙인다 —
  // 부서만 쓴 사람, 팀만 쓴 사람 모두 이 한 줄로 나온다.
  const affiliationLine = $derived(
    [user.organization_name, user.department, user.team]
      .filter(Boolean)
      .join(' · ')
  );

  // Follower/following modal
  let showFollowersModal = $state(false);
  let showFollowingModal = $state(false);
  // 🔴 `api/v1/follows_controller` 도 snake 다(`avatar_url`, :99). 카멜로 읽던
  //    탓에 팔로워·팔로잉 모달의 아바타도 그려진 적이 없었다.
  let modalUsers = $state<Array<{ id: number; name: string; nickname?: string; avatar_url?: string }>>([]);
  let isLoadingModal = $state(false);

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  async function loadFollowers() {
    isLoadingModal = true;
    showFollowersModal = true;
    const res = await apiClient.get<{ users: typeof modalUsers }>('/api/v1/follows/followers', {
      params: { userId: user.id }
    });
    if (res && typeof res === 'object' && 'users' in res) {
      modalUsers = (res as { users: typeof modalUsers }).users;
    }
    isLoadingModal = false;
  }

  async function loadFollowing() {
    isLoadingModal = true;
    showFollowingModal = true;
    const res = await apiClient.get<{ users: typeof modalUsers }>('/api/v1/follows/following', {
      params: { userId: user.id }
    });
    if (res && typeof res === 'object' && 'users' in res) {
      modalUsers = (res as { users: typeof modalUsers }).users;
    }
    isLoadingModal = false;
  }

  function closeModal() {
    showFollowersModal = false;
    showFollowingModal = false;
    modalUsers = [];
  }

  function handleModalBackdropKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closeModal();
    }
  }

  function handleFollowToggle(isFollowing: boolean) {
    if (user.stats) {
      user.stats.followers_count += isFollowing ? 1 : -1;
    }
  }
</script>

<svelte:head>
  <title>{user.nickname || user.name} | AX Admin</title>
</svelte:head>

<div class="space-y-6">
  <!-- Header Banner -->
  <div class="h-32 bg-gradient-to-r from-primary via-blue-500 to-indigo-600"></div>

  <main class="max-w-3xl mx-auto -mt-16">
    <!-- Profile Card -->
    <div class="card p-8 mb-6">
      <div class="flex items-end gap-6 mb-6">
        <!-- Avatar -->
        <div class="flex-shrink-0 -mt-16">
          <div class="w-28 h-28 rounded-2xl overflow-hidden bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center ring-4 ring-white dark:ring-dark-secondary shadow-lg">
            {#if user.avatar_url}
              <img src={user.avatar_url} alt={user.name} class="w-full h-full object-cover" />
            {:else}
              <span class="text-4xl font-bold text-white">
                {user.nickname?.charAt(0) || user.name?.charAt(0) || '?'}
              </span>
            {/if}
          </div>
        </div>

        <!-- Name + Actions -->
        <div class="flex-1 flex items-center justify-between">
          <div>
            <h1 class="text-h2 text-content flex items-center gap-2">
              {user.nickname || user.name}
              {#if user.verified}
                <svg class="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              {/if}
            </h1>
            <!-- 소속·부서·팀은 각각 따로 올 수 있다. 부서만, 팀만 채운 사용자도 표시된다. -->
            {#if affiliationLine}
              <p class="text-body2 text-content-tertiary mt-0.5">{affiliationLine}</p>
            {/if}
          </div>

          {#if !user.is_own_profile}
            <div class="flex items-center gap-2">
              <FollowButton
                userId={user.id}
                initialFollowing={user.is_following}
                onToggle={handleFollowToggle}
              />
              <BlockButton
                userId={user.id}
                initialBlocked={user.is_blocked}
              />
            </div>
          {:else}
            <a href="/profile" class="btn btn-secondary btn-sm">프로필 편집</a>
          {/if}
        </div>
      </div>

      {#if user.bio}
        <p class="text-body1 text-gray-700 dark:text-dark-text-medium mb-6">{user.bio}</p>
      {/if}

      <!-- Stats Row -->
      {#if user.stats}
        <div class="flex items-center gap-6 text-body2">
          <span class="text-content">
            <strong>{user.stats.posts_count || 0}</strong>
            <span class="text-content-tertiary ml-1">게시글</span>
          </span>
          <button onclick={loadFollowers} class="hover:underline text-content">
            <strong>{user.stats.followers_count || 0}</strong>
            <span class="text-content-tertiary ml-1">팔로워</span>
          </button>
          <button onclick={loadFollowing} class="hover:underline text-content">
            <strong>{user.stats.following_count || 0}</strong>
            <span class="text-content-tertiary ml-1">팔로잉</span>
          </button>
          <span class="text-content-tertiary">
            가입일: {formatDate(user.created_at)}
          </span>
        </div>
      {/if}
    </div>

    <!-- Activity Tabs (placeholder for future) -->
    <div class="card p-6">
      <div class="flex border-b border-default mb-6">
        <button class="px-4 pb-3 text-body2 font-medium text-primary border-b-2 border-primary">게시글</button>
        <button class="px-4 pb-3 text-body2 font-medium text-content-tertiary hover:text-gray-700">댓글</button>
        <button class="px-4 pb-3 text-body2 font-medium text-content-tertiary hover:text-gray-700">좋아요</button>
      </div>

      <div class="text-center py-12 text-content-tertiary">
        <svg class="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
        <p class="text-body2">아직 게시글이 없습니다.</p>
      </div>
    </div>
  </main>
</div>

<!-- Followers/Following Modal -->
{#if showFollowersModal || showFollowingModal}
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    onclick={closeModal}
    onkeydown={handleModalBackdropKeydown}
    role="button"
    aria-label="모달 닫기"
    tabindex="0"
  >
    <div class="bg-surface rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] overflow-hidden" onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" tabindex="0">
      <div class="flex items-center justify-between p-4 border-b border-default">
        <h3 class="text-h3 text-content">
          {showFollowersModal ? '팔로워' : '팔로잉'}
        </h3>
        <button onclick={closeModal} aria-label="닫기" class="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-high">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="overflow-y-auto max-h-[55vh] p-4">
        {#if isLoadingModal}
          <div class="text-center py-8">
            <svg class="animate-spin w-8 h-8 mx-auto text-primary" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          </div>
        {:else if modalUsers.length === 0}
          <p class="text-center py-8 text-content-tertiary text-body2">
            {showFollowersModal ? '팔로워가 없습니다.' : '팔로잉하는 사용자가 없습니다.'}
          </p>
        {:else}
          <div class="space-y-3">
            {#each modalUsers as modalUser}
              <a href="/users/{modalUser.id}" class="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center flex-shrink-0">
                  {#if modalUser.avatar_url}
                    <img src={modalUser.avatar_url} alt="" class="w-full h-full rounded-xl object-cover" />
                  {:else}
                    <span class="text-sm font-bold text-white">{modalUser.nickname?.charAt(0) || modalUser.name?.charAt(0) || '?'}</span>
                  {/if}
                </div>
                <div>
                  <p class="text-body2 font-medium text-content">{modalUser.nickname || modalUser.name}</p>
                </div>
              </a>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}
